import * as SelectPrimitive from '@radix-ui/react-select';
import { VariantProps } from 'class-variance-authority';
import { ComponentProps, createContext, MouseEvent, useContext, useEffect, useState } from 'react';

import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import { Label } from '@/components/common';
import { SELECTION_DEFAULT_VALUES } from '@/constants';
import { SelectionValueType } from '@/types';
import { mergeClasses } from '@/utils';

import { IOption } from '@/interfaces';
import { SELECT_CONTENT_VARIANTS, SELECT_VARIANTS } from './Dropdown.constants';

interface ISelectContextValue {
  onValueChange?: (value: string) => void;
  value?: string;
  error?: string;
  isOpen?: boolean;
  valueType?: SelectionValueType;
  excludedValues?: string[];
  disabled?: boolean;
}

interface ISelect
  extends ComponentProps<typeof SelectPrimitive.Root>,
    VariantProps<typeof SELECT_VARIANTS> {
  className?: string;
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  valueType?: SelectionValueType;
  excludedValues?: string[];
  disabled?: boolean;
}

interface IDropdown extends ISelect {
  options: IOption[];
  allowClear?: boolean;
  placeholder?: string;
}

const SelectContext = createContext<ISelectContextValue>({});

const Select = ({
  className,
  label,
  onValueChange: controlledOnValueChange,
  value: controlledValue,
  required,
  helperText,
  error,
  valueType = 'string',
  excludedValues,
  disabled,
  variant,
  onOpenChange,
  ...props
}: ISelect) => {
  const [isOpen, setOpen] = useState(false);
  const [value, setValue] = useState(controlledValue);

  useEffect(() => {
    setValue(controlledValue);
  }, [controlledValue]);

  const onValueChange = (value: string) => {
    setValue(value);
    controlledOnValueChange?.(value);
  };

  return (
    <SelectContext.Provider
      value={{ onValueChange, value, error, isOpen, valueType, excludedValues, disabled }}
    >
      <div className={mergeClasses(SELECT_VARIANTS({ variant }), className)}>
        {label && (
          <Label data-slot="select-label" required={required}>
            {label}
          </Label>
        )}
        <SelectPrimitive.Root
          key={value == SELECTION_DEFAULT_VALUES[valueType] ? 'empty' : value}
          data-slot="select"
          value={value == SELECTION_DEFAULT_VALUES[valueType] ? undefined : value}
          onValueChange={onValueChange}
          open={isOpen}
          onOpenChange={(open) => {
            setOpen(open);
            onOpenChange?.(open);
          }}
          required={required}
          disabled={disabled}
          {...props}
        />

        {error && (
          <p data-slot="select-bottom-text" className="text-semantic-error-base font-medium">
            {error}
          </p>
        )}
        {helperText && (
          <p data-slot="select-bottom-text" className="text-neutral-7 font-medium">
            {helperText}
          </p>
        )}
      </div>
    </SelectContext.Provider>
  );
};

const SelectGroup = ({ ...props }: ComponentProps<typeof SelectPrimitive.Group>) => {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
};

const SelectValue = ({ ...props }: ComponentProps<typeof SelectPrimitive.Value>) => {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
};

const SelectTrigger = ({
  className,
  allowClear = false,
  showChevron = true,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Trigger> & {
  allowClear?: boolean;
  showChevron?: boolean;
}) => {
  const { onValueChange, value, error, isOpen, valueType, disabled } = useContext(SelectContext);
  const showClearButton = allowClear && Boolean(value);

  const clearSelection = (e: MouseEvent) => {
    e.stopPropagation();
    onValueChange?.(SELECTION_DEFAULT_VALUES[valueType || 'string']);
  };

  return (
    <div className="relative inline-block w-full">
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        className={mergeClasses(
          "font-medium shadow-none rounded-xs border border-neutral-4 bg-neutral-0 text-neutral-9 data-[placeholder]:text-neutral-7 focus:border-primary-base data-[state=open]:border-primary-base data-[placeholder]:focus:text-neutral-7 w-full gap-2 px-4 py-2 whitespace-nowrap transition-[color,background-color,border-color,box-shadow] outline-none *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 dark:bg-neutral-10 dark:border-neutral-8 dark:text-neutral-0 dark:data-[placeholder]:text-neutral-6 dark:data-[placeholder]:focus:text-neutral-6 dark:focus:border-primary-base dark:data-[state=open]:border-primary-base flex flex-row !justify-between items-center",
          error && '!border-semantic-error-base',
          disabled
            ? '!bg-neutral-2 !border-neutral-4 !text-neutral-6 cursor-not-allowed dark:!bg-neutral-9 dark:!border-neutral-8 dark:text-neutral-7'
            : 'hover:border-neutral-6 data-[placeholder]:hover:text-primary-darkest dark:hover:border-neutral-7 dark:data-[placeholder]:hover:text-neutral-5',
          className,
        )}
        {...props}
      >
        {children}
        <div className="flex items-center gap-2">
          {showClearButton && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-neutral-6 hover:text-neutral-8 size-4 flex items-center justify-center transition-colors z-10 dark:text-neutral-6 dark:hover:text-neutral-4"
              aria-label="Clear selection"
            >
              <CloseIcon className="size-4" />
            </button>
          )}
          {showChevron && (
            <SelectPrimitive.Icon asChild>
              <div
                className={mergeClasses(
                  'size-4',
                  disabled
                    ? 'text-neutral-6 dark:text-neutral-7'
                    : 'text-neutral-9 dark:text-neutral-4',
                )}
              >
                {isOpen ? (
                  <ChevronUpIcon className="size-5 text-primary-base" />
                ) : (
                  <ChevronDownIcon className="size-5" />
                )}
              </div>
            </SelectPrimitive.Icon>
          )}
        </div>
      </SelectPrimitive.Trigger>
    </div>
  );
};

const SelectContent = ({
  className,
  children,
  position = 'popper',
  align = 'center',
  variant,
  ...props
}: ComponentProps<typeof SelectPrimitive.Content> & VariantProps<typeof SELECT_VARIANTS>) => {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={mergeClasses(
          SELECT_CONTENT_VARIANTS({ variant }),
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        align={align}
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#CED4DA transparent' }}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={mergeClasses(
            'py-2 !overflow-visible',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

const SelectItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Item>) => {
  const { excludedValues, value } = useContext(SelectContext);
  const isDisabled = excludedValues?.includes(props.value);
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={mergeClasses(
        "cursor-pointer outline-none pl-4 text-neutral-9 font-medium relative flex items-center justify-between gap-2 rounded-none select-none transition-colors data-[highlighted]:bg-neutral-2 data-[highlighted]:text-neutral-9 data-[state=checked]:bg-neutral-2 data-[state=checked]:text-neutral-9 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:text-neutral-1 dark:data-[highlighted]:bg-neutral-8 dark:data-[highlighted]:text-neutral-0 dark:data-[state=checked]:bg-neutral-8 dark:data-[state=checked]:text-neutral-0 dark:data-[state=checked]:[&_svg]:text-primary-light [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        isDisabled && '!cursor-not-allowed',
        value === props.value && '!text-primary-base',
        className,
      )}
      disabled={isDisabled}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <span
        data-slot="select-item-indicator"
        className="flex size-5 items-center justify-center text-primary-base"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-5" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
};

const Dropdown = ({
  options,
  allowClear,
  placeholder = 'Select',
  variant,
  ...props
}: IDropdown) => {
  return (
    <Select variant={variant} {...props}>
      <SelectTrigger allowClear={allowClear} value={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent variant={variant}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export { Dropdown, Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue };
